import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  NgModule,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
  distinctUntilChanged,
  fromEvent,
  map,
  Observable,
  tap,
  withLatestFrom,
} from 'rxjs';
import { UntilDestroy } from '@ngneat/until-destroy';
import { SvgIconComponentModule } from '@nx-icilalune/icilalune/media';
import { ComponentStore } from '@ngrx/component-store';
import { LetModule, PushModule } from '@ngrx/component';
import { PageVisibilityService } from '@icilalune/utils';
import { AudioService } from '@icilalune/utils/audio';
import {
  VimeoPauseEvent,
  VimeoPlayEvent,
} from '@nx-icilalune/icilalune/media/tokens';
import { BREAKPOINT } from '@icilalune/shell-state';
import { Breakpoint } from '@icilalune/utils/responsive';

export interface ToolBarState {
  playAudio: boolean;
  userAudioConsent: boolean | undefined;
  isLune: boolean;
  buttonsCreated?: boolean;
  luneReady?: boolean;
  buttonFirstMove: boolean;
}

@UntilDestroy()
@Component({
  selector: 'sh-ui-tool-bar',
  template: `
    <ul class="main-functions">
      <li class="tool-music-group">
        <ng-container *ngrxLet="userAudioConsent$; let userAudioConsent">
          <ng-container *ngrxLet="playAudio$; let playAudio">
            <button #audioButton class="toggle-btn" [class.active]="playAudio">
              <i2l-svg-icon
                [icon]="playAudio ? 'music_on' : 'music_off'"
              ></i2l-svg-icon>
            </button>
          </ng-container>
        </ng-container>
      </li>
      <li class="social small-icon">
        <a
          href="https://www.linkedin.com/company/icilalune-paris-montreal/"
          target="_blank"
        >
          <i2l-svg-icon icon="linkedin"></i2l-svg-icon>
        </a>
      </li>
      <li class="social small-icon">
        <a href="https://twitter.com/icilalune" target="_blank">
          <i2l-svg-icon icon="twitter"></i2l-svg-icon>
        </a>
      </li>
    </ul>
    <!--<ul>
      <li>
        <i2l-svg-icon [icon]="'volume-1'"></i2l-svg-icon>
      </li>
    </ul>
    <ul>
      <li>
        <i2l-svg-icon [icon]="'maximize'"></i2l-svg-icon>
      </li>
    </ul>-->
  `,
  styleUrls: ['./tool-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToolBarComponent
  extends ComponentStore<ToolBarState>
  implements AfterViewInit
{
  mute$: Observable<boolean> = this.audioService.mute$;

  userMute$: Observable<boolean> = this.audioService.userMute$.pipe(
    distinctUntilChanged(),
    withLatestFrom(this.mute$),
    tap(([userMute, mute]) => {
      if (userMute) {
        this.patchState({ playAudio: false });
      } else if (!userMute && !mute) {
        const userAudioConsent = this.get((s) => s.userAudioConsent);
        if (userAudioConsent === true) {
          this.patchState({ playAudio: true, userAudioConsent: true });
        }
      }
    }),
    map(([userMute, mute]) => {
      return userMute;
    })
  );

  readyForLune$: Observable<boolean> = this.select(
    (s) => !!s.luneReady && !!s.buttonsCreated && s.isLune
  );

  isLune$: Observable<boolean> = this.select((s) => s.isLune);

  userAudioConsent$ = this.select((s) => s.userAudioConsent);
  playAudio$ = this.select((s) => s.playAudio).pipe(distinctUntilChanged());

  @Input()
  standBy = false;

  @Output()
  playMusic = this.playAudio$;

  @Input()
  set isLune(isLune: boolean) {
    this.patchState({ isLune });
  }

  #audioButton!: ElementRef<HTMLButtonElement>;
  @ViewChild('audioButton')
  set audioButton(audioButton: ElementRef<HTMLButtonElement>) {
    this.#audioButton = audioButton;
    if (audioButton) {
      this.setUserAudioConsent(fromEvent(audioButton.nativeElement, 'click'));
      this.audioButtonsCreated();
    }
  }

  setUserAudioConsent = this.effect((click$: Observable<Event>) => {
    return click$.pipe(
      tap((event) => {
        const playAudio = !this.get((s) => s.playAudio);
        const userAudioConsent = !this.get((s) => s.userAudioConsent);
        this.patchState({
          userAudioConsent: userAudioConsent,
          playAudio: playAudio,
        });
        if (this.audioService.userMute) {
          this.audioService.userMute = false;
        }
      })
    );
  });

  setPageVisibility = this.effect((pageVisible$: Observable<boolean>) => {
    return pageVisible$.pipe(
      tap((pageVisible) => {
        if (!pageVisible) {
          this.audioService.mute = true;
        } else {
          this.audioService.mute = this.audioService.userMute;
        }
      })
    );
  });

  setLuneScreenReady = this.effect((event$: Observable<Event>) => {
    return event$.pipe(
      tap(() => {
        this.patchState({ luneReady: true });
      })
    );
  });

  setLuneScreenDestroyed = this.effect((event$: Observable<Event>) => {
    return event$.pipe(
      tap(() => {
        this.patchState({ luneReady: false });
      })
    );
  });

  checkForLune = this.effect((luneReady$: Observable<boolean>) => {
    return luneReady$.pipe(
      withLatestFrom(this.isLune$, this.breakpoint$),
      tap(([luneReady, isLune, breakpoint]) => {
        if (this.#audioButton) {
          let targetElement: Element | null = null;
          const firstMove = this.get((s) => s.buttonFirstMove);
          if (firstMove && isLune && breakpoint !== 'xs') {
            this.patchState({ buttonFirstMove: false });
          }
          const musicTargetClass =
            isLune && firstMove ? 'home-music-control' : 'tool-music-group';
          //
          targetElement = this.document
            .getElementsByClassName(musicTargetClass)
            .item(0);
          if (targetElement) {
            targetElement.appendChild(this.#audioButton.nativeElement);
          }
        }
      })
    );
  });

  checkMute = this.effect((mute$: Observable<boolean>) => {
    return mute$.pipe(
      tap((mute) => {
        if (mute) {
          this.patchState({ playAudio: false });
        } else {
          const userAudioConsent = this.get((s) => s.userAudioConsent);
          if (userAudioConsent === true) {
            this.patchState({ playAudio: true, userAudioConsent: true });
          }
        }
      })
    );
  });

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(BREAKPOINT)
    public breakpoint$: Observable<Breakpoint>,
    private pageVisibilityService: PageVisibilityService,
    private audioService: AudioService
  ) {
    super({
      playAudio: false,
      userAudioConsent: undefined,
      isLune: false,
      buttonFirstMove: true,
    });
    if (this.document.defaultView) {
      this.setLuneScreenReady(
        fromEvent(this.document.defaultView, 'luneScreenCreated')
      );
      this.setLuneScreenDestroyed(
        fromEvent(this.document.defaultView, 'luneScreenDestroyed')
      );
      this.document.defaultView.addEventListener(VimeoPlayEvent, () => {
        this.patchState({ playAudio: false });
      });
      this.document.defaultView.addEventListener(VimeoPauseEvent, () => {
        if (
          !this.audioService.mute &&
          !this.audioService.userMute &&
          this.get((s) => s.userAudioConsent)
        ) {
          this.patchState({ playAudio: true });
        }
      });
    }
    this.checkForLune(this.readyForLune$);
    this.checkMute(this.mute$);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.setPageVisibility(this.pageVisibilityService.pageVisible$);
    }, 0);
    /*combineLatest([this.currentMenuItemIsFirst$, this.currentMenuItemIsLast$])
      .pipe(untilDestroyed(this))
      .subscribe(([currentMenuItemIsFirst, currentMenuItemIsLast]) => {
        const { nativeElement } = this.el;
        this.renderer.removeClass(nativeElement, 'stand-by');
        if (!currentMenuItemIsFirst && !currentMenuItemIsLast) {
          this.renderer.addClass(nativeElement, 'stand-by');
        }
      });*/
  }

  audioButtonsCreated() {
    if (this.#audioButton) {
      this.patchState({ buttonsCreated: true });
    }
  }
}

@NgModule({
  imports: [CommonModule, SvgIconComponentModule, PushModule, LetModule],
  declarations: [ToolBarComponent],
  exports: [ToolBarComponent],
})
export class ToolBarComponentModule {}
